import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BASE_PATH = '/api/v1/tip';

const STAKE_REGEX = /^(\d+\.?\d*|\.\d+)$/;
const STAKE_MIN = 0.1;
const STAKE_MAX = 2.0;

const VALID_TOOLS = new Set(['SNIPE_FEED', 'ODDS_SCREEN', 'PROP_TOOL', 'HOT_PROPS', 'MULTI_BUILDER']);
// Tools that require a user-chosen bookmaker on SINGLE/LADDER tips.
const BOOKMAKER_TOOLS = new Set(['ODDS_SCREEN', 'PROP_TOOL']);
const VALID_TIP_TYPES = new Set(['SINGLE', 'LADDER']);
const VALID_MULTI_DATA_SOURCES = new Set(['SNIPE_FEED']);
const VALID_MULTI_DATASETS = new Set(['2026', '2025_2026']);

const getAuthHeaders = () => {
    const token = authService.getToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

/**
 * Normalises a stake-like value into a number. Returns null when the input
 * doesn't match the API-side validator (rejects letters/signs/exponents).
 */
export const parseStake = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null;
        return Math.round(value * 100) / 100;
    }
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!STAKE_REGEX.test(trimmed)) return null;
    const num = Number(trimmed);
    if (!Number.isFinite(num)) return null;
    return Math.round(num * 100) / 100;
};

export const isStakeInRange = (num) => {
    return typeof num === 'number' && num >= STAKE_MIN && num <= STAKE_MAX;
};

export const STAKE_LIMITS = { min: STAKE_MIN, max: STAKE_MAX };

const handleApiError = (error) => {
    if (error.response) {
        const { status, data } = error.response;
        const errorCode = data?.error || 'SERVER_ERROR';
        const baseMessage = data?.message;

        if (status === 400) {
            const fieldErrors = Array.isArray(data?.errors) ? data.errors : [];
            return {
                success: false,
                status,
                errorCode: errorCode || 'VALIDATION_ERROR',
                message: baseMessage || 'Invalid tip submission',
                fieldErrors,
                isValidationError: true,
            };
        }

        if (status === 401) {
            return {
                success: false,
                status,
                errorCode: errorCode || 'UNAUTHORIZED',
                message: baseMessage || 'Authentication failed',
                isAuthError: true,
            };
        }

        if (status === 403) {
            const isRoleError = errorCode === 'ROLE_REQUIRED';
            const isSubscriptionError = errorCode === 'SUBSCRIPTION_REQUIRED';
            return {
                success: false,
                status,
                errorCode,
                message: baseMessage || (isRoleError
                    ? 'The COMMUNITY_TIPPER role is required to submit community tips.'
                    : 'An active paid subscription is required to submit community tips.'),
                isRoleError,
                isSubscriptionError,
            };
        }

        if (status === 404 && errorCode === 'TIP_DATA_NOT_FOUND') {
            return {
                success: false,
                status,
                errorCode,
                message: baseMessage || 'This prop is not currently available. Data may be updating.',
                isNotFound: true,
            };
        }

        if (status === 409 && errorCode === 'DUPLICATE_TIP') {
            return {
                success: false,
                status,
                errorCode,
                message: baseMessage || 'A community tip already exists for this prop in the current round.',
                isDuplicate: true,
            };
        }

        if (status === 422) {
            if (errorCode === 'INVALID_BOOKMAKER') {
                return {
                    success: false,
                    status,
                    errorCode,
                    message: baseMessage || 'Selected bookmaker does not have a price for this prop.',
                    isInvalidBookmaker: true,
                    availableBookmakers: Array.isArray(data?.available_bookmakers) ? data.available_bookmakers : [],
                };
            }
            if (errorCode === 'LADDER_MARKETS_INVALID') {
                return {
                    success: false,
                    status,
                    errorCode,
                    message: baseMessage || 'Ladder markets must all belong to the same category.',
                    isLadderError: true,
                };
            }
            return {
                success: false,
                status,
                errorCode,
                message: baseMessage || 'Request could not be processed.',
            };
        }

        if (status === 429) {
            return {
                success: false,
                status,
                errorCode: errorCode || 'RATE_LIMIT_EXCEEDED',
                message: baseMessage || 'Too many tip submissions. Please slow down and try again in a minute.',
                isRateLimited: true,
            };
        }

        if (status === 500) {
            return {
                success: false,
                status,
                errorCode: errorCode || 'SERVER_ERROR',
                message: baseMessage || 'Server error. Please try again later.',
            };
        }

        if (status === 503) {
            const isNoData = errorCode === 'NO_DATA';
            return {
                success: false,
                status,
                errorCode: errorCode || 'SERVICE_UNAVAILABLE',
                message: baseMessage || (isNoData
                    ? 'No tip data is currently available. Data may be updating.'
                    : 'Tip service is temporarily unavailable. Please try again later.'),
                isNoData,
            };
        }

        return {
            success: false,
            status,
            errorCode,
            message: baseMessage || 'Failed to submit tip.',
        };
    }

    return {
        success: false,
        status: null,
        errorCode: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please check your internet connection.',
    };
};

const buildSinglePayload = ({ tipped_tool, player_name, market, bookmaker, stake }) => {
    const payload = {
        tipped_tool,
        tip_type: 'SINGLE',
        tip_data_player_name: String(player_name || '').trim(),
        tip_data_player_market: String(market || '').trim(),
        tipped_unit_stake: stake,
    };
    if (BOOKMAKER_TOOLS.has(tipped_tool) && bookmaker) {
        payload.tip_data_bookmaker = String(bookmaker).trim().toLowerCase();
    }
    return payload;
};

const buildLadderPayload = ({ tipped_tool = 'ODDS_SCREEN', player_name, bookmaker, legs }) => {
    const payload = {
        tipped_tool: BOOKMAKER_TOOLS.has(tipped_tool) ? tipped_tool : 'ODDS_SCREEN',
        tip_type: 'LADDER',
        tip_data_player_name: String(player_name || '').trim(),
        tip_data_player_market: legs.map((leg) => ({
            market: String(leg.market || '').trim(),
            stake: leg.stake,
        })),
    };
    if (bookmaker) {
        payload.tip_data_bookmaker = String(bookmaker).trim().toLowerCase();
    }
    return payload;
};

/**
 * Multi Builder payload — per the v2 API docs, MULTI_BUILDER tips:
 *  - Do **not** include `tip_type` (it's an implicit `MULTI`; sending anything
 *    other than `"MULTI"` triggers a 400 on the server).
 *  - Do **not** use the `tip_data_player_*` SINGLE fields. Each leg has its
 *    own `{ player_name, player_market }` tuple inside `legs[]`.
 *  - Use a single `tipped_unit_stake` for the whole multi (no per-leg stakes).
 *  - Carry `data_source` (currently locked to `"SNIPE_FEED"`) and `dataset`
 *    (`"2026"` or `"2025_2026"`), which the backend uses to mirror the
 *    in-app season toggle when picking each leg's value opportunity.
 */
const buildMultiBuilderPayload = ({ data_source = 'SNIPE_FEED', dataset, legs, stake }) => {
    return {
        tipped_tool: 'MULTI_BUILDER',
        data_source: String(data_source).toUpperCase(),
        dataset: String(dataset),
        tipped_unit_stake: stake,
        legs: legs.map((leg) => ({
            player_name: String(leg.player_name || '').trim(),
            player_market: String(leg.player_market || '').trim(),
        })),
    };
};

const validateSingleClientSide = ({ tipped_tool, player_name, market, bookmaker, stake }) => {
    if (!VALID_TOOLS.has(tipped_tool)) {
        return { ok: false, message: 'Invalid tool selected.' };
    }
    if (!player_name || !String(player_name).trim()) {
        return { ok: false, message: 'Player is required.' };
    }
    if (!market || !String(market).trim()) {
        return { ok: false, message: 'Market is required.' };
    }
    if (BOOKMAKER_TOOLS.has(tipped_tool) && (!bookmaker || !String(bookmaker).trim())) {
        return { ok: false, message: 'Bookmaker is required for this tip.' };
    }
    const parsed = parseStake(stake);
    if (parsed == null) {
        return { ok: false, message: 'Stake must be a plain number (e.g. 0.5, 1, 1.5).' };
    }
    if (!isStakeInRange(parsed)) {
        return { ok: false, message: `Stake must be between ${STAKE_MIN} and ${STAKE_MAX} units.` };
    }
    return { ok: true, stake: parsed };
};

const validateMultiBuilderClientSide = ({ data_source = 'SNIPE_FEED', dataset, legs, stake }) => {
    if (!VALID_MULTI_DATA_SOURCES.has(String(data_source).toUpperCase())) {
        return { ok: false, message: 'Unsupported multi data source.' };
    }
    if (!dataset || !VALID_MULTI_DATASETS.has(String(dataset))) {
        return { ok: false, message: 'Unsupported multi dataset.' };
    }
    if (!Array.isArray(legs) || legs.length < 2) {
        return { ok: false, message: 'Multi tips need at least 2 legs.' };
    }
    if (legs.length > 10) {
        return { ok: false, message: 'Multi tips can have at most 10 legs.' };
    }
    const seen = new Set();
    const cleaned = [];
    for (let i = 0; i < legs.length; i += 1) {
        const leg = legs[i] || {};
        const player = String(leg.player_name || '').trim();
        const market = String(leg.player_market || '').trim();
        if (!player) return { ok: false, message: `Leg ${i + 1}: player is required.` };
        if (!market) return { ok: false, message: `Leg ${i + 1}: market is required.` };
        const key = `${player.toLowerCase()}|${market.toLowerCase()}`;
        if (seen.has(key)) {
            return { ok: false, message: `Leg ${i + 1}: duplicate (player, market) tuple.` };
        }
        seen.add(key);
        cleaned.push({ player_name: player, player_market: market });
    }
    const parsed = parseStake(stake);
    if (parsed == null) {
        return { ok: false, message: 'Stake must be a plain number (e.g. 0.5, 1, 1.5).' };
    }
    if (!isStakeInRange(parsed)) {
        return { ok: false, message: `Stake must be between ${STAKE_MIN} and ${STAKE_MAX} units.` };
    }
    return { ok: true, legs: cleaned, stake: parsed };
};

const validateLadderClientSide = ({ player_name, bookmaker, legs }) => {
    if (!player_name || !String(player_name).trim()) {
        return { ok: false, message: 'Player is required.' };
    }
    if (!bookmaker || !String(bookmaker).trim()) {
        return { ok: false, message: 'Bookmaker is required for ladder tips.' };
    }
    if (!Array.isArray(legs) || legs.length < 2) {
        return { ok: false, message: 'Ladder tips need at least 2 legs.' };
    }
    if (legs.length > 10) {
        return { ok: false, message: 'Ladder tips can have at most 10 legs.' };
    }
    const seen = new Set();
    const cleaned = [];
    for (let i = 0; i < legs.length; i += 1) {
        const leg = legs[i];
        const m = String(leg?.market || '').trim();
        if (!m) {
            return { ok: false, message: `Leg ${i + 1}: market is required.` };
        }
        const key = m.toLowerCase();
        if (seen.has(key)) {
            return { ok: false, message: `Leg ${i + 1}: duplicate market.` };
        }
        seen.add(key);
        const parsed = parseStake(leg?.stake);
        if (parsed == null) {
            return { ok: false, message: `Leg ${i + 1}: stake must be a plain number.` };
        }
        if (!isStakeInRange(parsed)) {
            return { ok: false, message: `Leg ${i + 1}: stake must be between ${STAKE_MIN} and ${STAKE_MAX} units.` };
        }
        cleaned.push({ market: m, stake: parsed });
    }
    return { ok: true, legs: cleaned };
};

const communityTipsService = {
    submitTip: async (input = {}) => {
        try {
            let payload;
            if (input.tipped_tool === 'MULTI_BUILDER') {
                const v = validateMultiBuilderClientSide(input);
                if (!v.ok) {
                    return {
                        success: false,
                        errorCode: 'CLIENT_VALIDATION',
                        message: v.message,
                        isClientValidationError: true,
                    };
                }
                payload = buildMultiBuilderPayload({
                    data_source: input.data_source,
                    dataset: input.dataset,
                    legs: v.legs,
                    stake: v.stake,
                });
            } else if (input.tip_type === 'LADDER') {
                const v = validateLadderClientSide(input);
                if (!v.ok) {
                    return {
                        success: false,
                        errorCode: 'CLIENT_VALIDATION',
                        message: v.message,
                        isClientValidationError: true,
                    };
                }
                payload = buildLadderPayload({
                    tipped_tool: input.tipped_tool,
                    player_name: input.player_name,
                    bookmaker: input.bookmaker,
                    legs: v.legs,
                });
            } else {
                const v = validateSingleClientSide(input);
                if (!v.ok) {
                    return {
                        success: false,
                        errorCode: 'CLIENT_VALIDATION',
                        message: v.message,
                        isClientValidationError: true,
                    };
                }
                payload = buildSinglePayload({
                    tipped_tool: input.tipped_tool,
                    player_name: input.player_name,
                    market: input.market,
                    bookmaker: input.bookmaker,
                    stake: v.stake,
                });
            }

            const response = await axios.post(`${API_URL}${BASE_PATH}`, payload, {
                headers: getAuthHeaders(),
            });

            const body = response.data;
            if (!body || body.success === false) {
                return {
                    success: false,
                    errorCode: body?.error || 'SERVER_ERROR',
                    message: body?.message || 'Failed to submit tip.',
                };
            }

            return {
                success: true,
                tips: Array.isArray(body?.data?.tips) ? body.data.tips : [],
                alert: body?.data?.alert || null,
            };
        } catch (error) {
            return handleApiError(error);
        }
    },
    parseStake,
    isStakeInRange,
    STAKE_LIMITS,
    VALID_TIP_TYPES: Array.from(VALID_TIP_TYPES),
};

export default communityTipsService;
